import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Spinner } from '@/components/ui/Spinner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { useAuth } from '@/contexts/AuthContext';
import { FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY } from '@/components/FlipkartAdsOauthConnections';
import { cn } from '@/utils/cn';
import { adsApi, brandsApi, flipkartAdsApi } from '@/api/client';
import { isCampaignStreamPath } from '@/api/managerAccounts';
import {
  encodeWorkspaceAccountValue,
  parseWorkspaceScope,
  pathAfterWorkspaceAccountSwitch,
  sameWorkspaceAccount,
  selectableAmazonAccountsFromProfiles,
  selectableBrandAccounts,
  selectableFlipkartAccounts,
  type WorkspaceAccountOption,
  type WorkspaceScope,
} from '@/api/workspaceScope';

const LAST_SCOPE_KEY = 'amz.lastWorkspaceScope.v1';
const LAST_PROFILE_KEY = 'amz.lastProfileId.v1';

const triggerClassName =
  'inline-flex h-[34px] max-w-[min(320px,50vw)] min-w-0 items-center gap-2 truncate rounded-lg border border-border bg-surface px-3 text-left text-body-md text-foreground shadow-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

function readLastScope(): WorkspaceScope | null {
  try {
    const raw = sessionStorage.getItem(LAST_SCOPE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<WorkspaceScope> & {
        credentialId?: string;
      };
      if (parsed.platform === 'amazon' && typeof parsed.accountId === 'string' && parsed.accountId) {
        return { platform: 'amazon', accountId: parsed.accountId };
      }
      if (
        parsed.platform === 'flipkart' &&
        typeof parsed.accountId === 'string' &&
        parsed.accountId &&
        typeof parsed.credentialId === 'string' &&
        parsed.credentialId
      ) {
        return {
          platform: 'flipkart',
          accountId: parsed.accountId,
          credentialId: parsed.credentialId,
        };
      }
      if (parsed.platform === 'brand' && typeof parsed.accountId === 'string' && parsed.accountId) {
        return { platform: 'brand', accountId: parsed.accountId };
      }
    }
  } catch {
    // sessionStorage may be unavailable or invalid
  }
  try {
    const profileId = sessionStorage.getItem(LAST_PROFILE_KEY);
    if (profileId && profileId.trim()) {
      return { platform: 'amazon', accountId: profileId };
    }
  } catch {
    // sessionStorage may be unavailable
  }
  return null;
}

function writeLastScope(scope: WorkspaceScope): void {
  try {
    sessionStorage.setItem(LAST_SCOPE_KEY, JSON.stringify(scope));
    if (scope.platform === 'amazon') {
      sessionStorage.setItem(LAST_PROFILE_KEY, scope.accountId);
    }
  } catch {
    // sessionStorage may be unavailable
  }
}

function AmazonCountryBadge({ code }: { code: string }) {
  return (
    <span
      className="inline-flex h-[18px] w-6 shrink-0 items-center justify-center rounded-[3px] bg-[#e8eef3] text-[10px] font-semibold leading-none tracking-wide text-[#4a5d6e]"
      aria-hidden
    >
      {code}
    </span>
  );
}

function AccountOptionRow({
  account,
  selected,
  onSelect,
}: {
  account: WorkspaceAccountOption;
  selected: boolean;
  onSelect: (account: WorkspaceAccountOption) => void;
}) {
  const showBadge = account.platform === 'amazon' && Boolean(account.countryCode);
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-body-md',
        selected ? 'bg-brand text-white' : 'text-foreground hover:bg-surface-muted'
      )}
      onClick={() => onSelect(account)}
    >
      {showBadge ? <AmazonCountryBadge code={account.countryCode!} /> : null}
      <span className="min-w-0 truncate">{account.label}</span>
    </button>
  );
}

export function AccountSwitcher() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDemo = Boolean(user?.is_demo);
  const fromPath = parseWorkspaceScope(location.pathname);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const scope = parseWorkspaceScope(location.pathname);
    if (scope) writeLastScope(scope);
  }, [location.pathname]);
  const activeScope = fromPath ?? readLastScope();
  const deferAccounts = isCampaignStreamPath(location.pathname);
  const [accountsRequested, setAccountsRequested] = useState(!deferAccounts);

  useEffect(() => {
    setAccountsRequested(!isCampaignStreamPath(location.pathname));
  }, [location.pathname]);

  const requestAccounts = () => setAccountsRequested(true);

  const amazonQuery = useQuery({
    queryKey: ['advertising-profiles'],
    queryFn: async () => {
      const res = await adsApi.getAdvertisingProfiles();
      if (!res.success) throw new Error('Failed to load accounts');
      return res.data;
    },
    enabled: accountsRequested,
  });

  const flipkartQuery = useQuery({
    queryKey: FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY,
    queryFn: async () => {
      const res = await flipkartAdsApi.listAllAccounts();
      if (!res.success) throw new Error('Failed to load Flipkart accounts');
      return res.data.accounts;
    },
    enabled: accountsRequested && !isDemo,
  });

  const amazonAccounts = selectableAmazonAccountsFromProfiles(amazonQuery.data);
  const brandsQuery = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      const res = await brandsApi.list();
      if (!res.success || !res.data) throw new Error('Failed to load brands');
      return res.data.brands;
    },
    enabled: accountsRequested && !isDemo,
  });

  const flipkartAccounts = selectableFlipkartAccounts(flipkartQuery.data ?? []);
  const brandAccounts = selectableBrandAccounts(brandsQuery.data ?? []);
  const allAccounts: WorkspaceAccountOption[] = [
    ...amazonAccounts,
    ...flipkartAccounts,
    ...brandAccounts,
  ];
  const activeAccount =
    activeScope && allAccounts.find((account) => sameWorkspaceAccount(activeScope, account));
  const activeValue = activeAccount ? encodeWorkspaceAccountValue(activeAccount) : '';
  const closedLabel = activeAccount?.label ?? (activeScope ? activeScope.accountId : 'Select account…');

  if (!accountsRequested) {
    return (
      <label className="inline-flex min-w-0 items-center gap-2">
        <span className="shrink-0 text-body-md text-foreground-muted">Account</span>
        <button
          type="button"
          className={triggerClassName}
          aria-label="Advertising account"
          onFocus={requestAccounts}
          onMouseDown={requestAccounts}
          onClick={() => {
            requestAccounts();
            setOpen(true);
          }}
        >
          <span className="min-w-0 flex-1 truncate">{closedLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-foreground-muted" aria-hidden />
        </button>
      </label>
    );
  }

  const amazonPending = amazonQuery.isLoading;
  const flipkartPending = !isDemo && flipkartQuery.isLoading;
  const brandsPending = !isDemo && brandsQuery.isLoading;
  if ((amazonPending || flipkartPending || brandsPending) && allAccounts.length === 0) {
    return (
      <span className="inline-flex items-center gap-2 text-foreground-muted">
        <Spinner className="h-4 w-4" />
        <span className="sr-only">Loading accounts</span>
      </span>
    );
  }

  if (allAccounts.length === 0) {
    return null;
  }

  const selectAccount = (account: WorkspaceAccountOption | null) => {
    setOpen(false);
    if (!account) {
      navigate('/accounts');
      return;
    }
    const target = pathAfterWorkspaceAccountSwitch(account, location.pathname);
    navigate(target, { state: { accountName: account.label } });
  };

  return (
    <div className="inline-flex min-w-0 items-center gap-2">
      <span className="shrink-0 text-body-md text-foreground-muted">Account</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={triggerClassName}
            aria-label="Advertising account"
            aria-haspopup="listbox"
            aria-expanded={open}
          >
            <span className="min-w-0 flex-1 truncate">{activeAccount?.label || 'Select account…'}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-foreground-muted" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[90] w-[var(--radix-popover-trigger-width)] min-w-[min(320px,50vw)] rounded-lg p-0"
        >
          <div className="max-h-72 overflow-y-auto py-1" role="listbox" aria-label="Advertising accounts">
            <button
              type="button"
              role="option"
              aria-selected={!activeValue}
              className={cn(
                'flex w-full px-3 py-1.5 text-left text-body-md',
                !activeValue
                  ? 'bg-brand text-white'
                  : 'text-foreground-muted hover:bg-surface-muted'
              )}
              onClick={() => selectAccount(null)}
            >
              Select account…
            </button>
            {amazonAccounts.length > 0 ? (
              <div role="group" aria-label="Amazon">
                <div className="px-3 pb-0.5 pt-1.5 text-body-md font-semibold text-foreground">
                  Amazon
                </div>
                {amazonAccounts.map((account) => (
                  <AccountOptionRow
                    key={encodeWorkspaceAccountValue(account)}
                    account={account}
                    selected={activeValue === encodeWorkspaceAccountValue(account)}
                    onSelect={selectAccount}
                  />
                ))}
              </div>
            ) : null}
            {flipkartAccounts.length > 0 ? (
              <div role="group" aria-label="Flipkart">
                <div className="px-3 pb-0.5 pt-1.5 text-body-md font-semibold text-foreground">
                  Flipkart
                </div>
                {flipkartAccounts.map((account) => (
                  <AccountOptionRow
                    key={encodeWorkspaceAccountValue(account)}
                    account={account}
                    selected={activeValue === encodeWorkspaceAccountValue(account)}
                    onSelect={selectAccount}
                  />
                ))}
              </div>
            ) : null}
            {brandAccounts.length > 0 ? (
              <div role="group" aria-label="Brands">
                <div className="px-3 pb-0.5 pt-1.5 text-body-md font-semibold text-foreground">
                  Brands
                </div>
                {brandAccounts.map((account) => (
                  <AccountOptionRow
                    key={encodeWorkspaceAccountValue(account)}
                    account={account}
                    selected={activeValue === encodeWorkspaceAccountValue(account)}
                    onSelect={selectAccount}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
